// The entity type registry: the one place that declares which entity types
// the frontend knows. It holds metadata only and imports no component.
// Adding a type is one entry here, one Detail component, and one line in the
// views map of components/Layout/entityDetailViews.ts.

import type { SvgIconComponent } from "@mui/icons-material";
import ArticleIcon from "@mui/icons-material/Article";
import ProjectIcon from "@mui/icons-material/Assignment";
import BusinessIcon from "@mui/icons-material/Business";
import CategoryIcon from "@mui/icons-material/Category";
import CapabilityPartIcon from "@mui/icons-material/Extension";
import FactoryIcon from "@mui/icons-material/Factory";
import ProductIcon from "@mui/icons-material/Inventory2";
import ClassIcon from "@mui/icons-material/LabelOutlined";
import IncidentIcon from "@mui/icons-material/ReportProblem";
import KindIcon from "@mui/icons-material/Widgets";
import type React from "react";
import {
  SearchFilters,
  SearchResponse,
  searchFacilities,
  searchOrganizations,
  searchProjects,
  searchSources,
} from "../services/searchApi";

// Called when an entity link is clicked. The entity type is optional so that
// existing `(entityId) => void` handlers stay assignable.
export type EntityClickHandler = (
  entityId: string,
  entityType?: string,
) => void;

// Where a detail is shown: in a side overlay, or as a page of its own
export type EntityDetailMode = "overlay" | "standalone";

// The props every type's Detail component receives from the dispatcher
export interface EntityDetailProps {
  entityId: string;
  mode?: EntityDetailMode;
  onEntityClick?: EntityClickHandler;
  onClose?: () => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

// Which header actions a type allows. The feature checks stay in the header.
export interface EntityTypeActions {
  map: boolean;
  graph: boolean;
  portfolio: boolean;
  track: boolean;
}

export interface EntityTypeEntry<
  K extends string = string,
  P extends string = string,
> {
  // The singular type key, equal to the entry's key in the registry
  key: K;
  // Lower-case singular name, e.g. "organization"
  singular: string;
  // Lower-case plural name: the value of the search tab and of the
  // `entityType` address parameter, e.g. "organizations"
  plural: P;
  // Display name of one entity, e.g. "Organization"
  label: string;
  // Display name of many, the search tab's label, e.g. "Organizations"
  pluralLabel: string;
  // The icon the detail header shows
  icon: SvgIconComponent;
  // The header actions the type allows
  actions: EntityTypeActions;
  // Whether the type's view offers "make root" in the header's extra actions
  allowsMakeRoot?: boolean;
  // Whether an address names the type beside the id. Set for a type whose
  // ids are opaque. Unset for the types an id alone resolves: organization,
  // facility, project and source.
  typeInAddress?: boolean;
  // The search function behind the type's search tab; a type without one
  // has no search tab
  search?: (filters: SearchFilters) => Promise<SearchResponse>;
}

// Keeps the keys and plurals as literal types (TypeScript 4.9 has no const
// type parameters) and checks that each entry's `key` equals its own key.
const defineEntityTypes = <K extends string, P extends string>(entries: {
  [Key in K]: EntityTypeEntry<Key, P>;
}) => entries;

// The order of the entries is the order of the search tabs
export const ENTITY_TYPES = defineEntityTypes({
  organization: {
    key: "organization",
    singular: "organization",
    plural: "organizations",
    label: "Organization",
    pluralLabel: "Organizations",
    icon: BusinessIcon,
    actions: { map: true, graph: true, portfolio: true, track: true },
    search: searchOrganizations,
  },
  facility: {
    key: "facility",
    singular: "facility",
    plural: "facilities",
    label: "Facility",
    pluralLabel: "Facilities",
    icon: FactoryIcon,
    actions: { map: true, graph: false, portfolio: true, track: false },
    search: searchFacilities,
  },
  project: {
    key: "project",
    singular: "project",
    plural: "projects",
    label: "Project",
    pluralLabel: "Projects",
    icon: ProjectIcon,
    actions: { map: false, graph: false, portfolio: true, track: false },
    search: searchProjects,
  },
  source: {
    key: "source",
    singular: "source",
    plural: "sources",
    label: "Source",
    pluralLabel: "Sources",
    icon: ArticleIcon,
    actions: { map: true, graph: false, portfolio: true, track: false },
    search: searchSources,
  },
  // A product of the product plane. Its ids are opaque, so its type travels
  // with its id. It has no search tab here: products are listed in Explore.
  product: {
    key: "product",
    singular: "product",
    plural: "products",
    label: "Product",
    pluralLabel: "Products",
    icon: ProductIcon,
    actions: { map: false, graph: false, portfolio: false, track: false },
    typeInAddress: true,
  },
  // The further types of the product plane. Like the product, each has
  // opaque ids, so its type travels with its id, and none has a search tab.
  // A class of products
  category: {
    key: "category",
    singular: "category",
    plural: "categories",
    label: "Category",
    pluralLabel: "Categories",
    icon: CategoryIcon,
    actions: { map: false, graph: false, portfolio: false, track: false },
    typeInAddress: true,
  },
  // A part of a capability claim: an ability, a condition, a mode, an
  // object or a means
  capability_part: {
    key: "capability_part",
    singular: "capability part",
    plural: "capability_parts",
    label: "Capability part",
    pluralLabel: "Capability parts",
    icon: CapabilityPartIcon,
    actions: { map: false, graph: false, portfolio: false, track: false },
    typeInAddress: true,
  },
  incident: {
    key: "incident",
    singular: "incident",
    plural: "incidents",
    label: "Incident",
    pluralLabel: "Incidents",
    icon: IncidentIcon,
    actions: { map: false, graph: false, portfolio: false, track: false },
    typeInAddress: true,
  },
  // A kind of the value chain: a curated class of type rank, named by its
  // category id. Its view adds "make root" in its own head.
  kind: {
    key: "kind",
    singular: "kind",
    plural: "kinds",
    label: "Kind",
    pluralLabel: "Kinds",
    icon: KindIcon,
    actions: { map: false, graph: false, portfolio: false, track: false },
    allowsMakeRoot: true,
    typeInAddress: true,
  },
  // A class of the value chain: a class its products are stated as, named by
  // its category id
  class: {
    key: "class",
    singular: "class",
    plural: "classes",
    label: "Class",
    pluralLabel: "Classes",
    icon: ClassIcon,
    actions: { map: false, graph: false, portfolio: false, track: false },
    allowsMakeRoot: true,
    typeInAddress: true,
  },
});

// The union of the singular type keys, e.g. "organization"
export type EntityTypeKey = keyof typeof ENTITY_TYPES;

// The union of the plural names of every type, e.g. "products"
export type AnyEntityTypePlural =
  (typeof ENTITY_TYPES)[EntityTypeKey]["plural"];

// The plural names of the types that have no tab in the search: a type
// entered without a `search` is named here as well
type UnsearchedEntityTypePlural =
  | "products"
  | "categories"
  | "capability_parts"
  | "incidents"
  | "kinds"
  | "classes";

// The union of the plural names of the types with a search tab, e.g.
// "organizations": the values of the search tab and of the search hook
export type EntityTypePlural = Exclude<
  AnyEntityTypePlural,
  UnsearchedEntityTypePlural
>;

export type RegisteredEntityType = EntityTypeEntry<
  EntityTypeKey,
  AnyEntityTypePlural
>;

// Every entry, in registry order
export const ENTITY_TYPE_LIST: RegisteredEntityType[] =
  Object.values(ENTITY_TYPES);

// The entries that have a search tab, in tab order
export const SEARCHABLE_ENTITY_TYPES: RegisteredEntityType[] =
  ENTITY_TYPE_LIST.filter((entry) => entry.search !== undefined);

// Looks an entry up by its singular key; undefined for an unknown type
export const getEntityTypeEntry = (
  entityType: string | null | undefined,
): RegisteredEntityType | undefined =>
  entityType && Object.prototype.hasOwnProperty.call(ENTITY_TYPES, entityType)
    ? ENTITY_TYPES[entityType as EntityTypeKey]
    : undefined;

// Looks an entry up by its plural name; undefined for an unknown type
export const getEntityTypeByPlural = (
  plural: string | null | undefined,
): RegisteredEntityType | undefined =>
  ENTITY_TYPE_LIST.find((entry) => entry.plural === plural);
